/**
 * Pusat Bantuan (Help Center) data composable
 * Provides all static data for the help center pages
 */

export interface HelpCategory {
  slug: string
  title: string
  description: string
  icon: string
}

export interface HelpArticle {
  slug: string
  category: string
  tab: 'faq' | 'artikel-panduan' | 'video-panduan'
  question: string
  answer: string
  answerComponent?: 'MdrRateTable'
  updatedAt: string
}

export interface HelpTab {
  value: 'faq' | 'artikel-panduan' | 'video-panduan'
  label: string
}

export function usePusatBantuanData() {
  const tabs: HelpTab[] = [
    { value: 'faq', label: 'FAQ' },
    { value: 'artikel-panduan', label: 'Artikel Panduan' },
    { value: 'video-panduan', label: 'Video Panduan' },
  ]

  const categories: HelpCategory[] = [
    {
      slug: 'akun-login-rintisan',
      title: 'Akun & Login Rintisan',
      description: 'Kelola akun, ubah kata sandi, dan atur keamanan login kamu.',
      icon: 'UserCircle',
    },
    {
      slug: 'profil-pengaturan-bisnis',
      title: 'Profil & Pengaturan Bisnis',
      description: 'Perbarui informasi bisnis, logo, dan data perusahaan kamu.',
      icon: 'BuildingOffice',
    },
    {
      slug: 'penagihan-pembayaran-invoice',
      title: 'Penagihan & Pembayaran Invoice',
      description: 'Pelajari cara membuat, mengirim, dan memantau status invoice ke klien.',
      icon: 'DocumentInvoice',
    },
    {
      slug: 'layanan-dan-fitur-rintisan',
      title: 'Layanan dan Fitur Rintisan',
      description: 'Kenali semua fitur yang tersedia dan cara memaksimalkannya untuk bisnismu.',
      icon: 'LayananFitur',
    },
    {
      slug: 'afiliasi-poin',
      title: 'Afiliasi & Poin',
      description: 'Pahami cara kerja program afiliasi, kode referral, dan penukaran poin RINT.',
      icon: 'AffiliatePoint',
    },
    {
      slug: 'partner-rintisan-dan-kasheer',
      title: 'Partner Rintisan dan Kasheer',
      description: 'Informasi seputar program partner dan integrasi dengan aplikasi Kasheer.',
      icon: 'PartnerKasheer',
    },
  ]

  const articles: HelpArticle[] = [
    // Akun & Login Rintisan
    {
      slug: 'bagaimana-cara-mendaftar-akun-rintisan-billing',
      category: 'akun-login-rintisan',
      tab: 'faq',
      question: 'Bagaimana cara mendaftar akun Rintisan Billing?',
      answer: `<p>Daftar akun Rintisan Billing cukup mudah. Kamu hanya perlu menyiapkan email aktif, lalu ikuti langkah berikut:</p>
<ol class="list-decimal list-inside space-y-2 mt-2">
  <li>Buka halaman pendaftaran Rintisan Billing</li>
  <li>Masukkan email kamu</li>
  <li>Isi nama lengkap, nomor telepon, kata sandi, dan konfirmasi kata sandi</li>
  <li>Jika punya kode referral, masukkan di kolom yang tersedia (opsional)</li>
  <li>Centang persetujuan Syarat &amp; Ketentuan</li>
  <li>Klik Daftar</li>
</ol>
<p class="mt-4">Setelah mendaftar, cek email kamu untuk verifikasi akun.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'mengapa-akun-saya-tidak-bisa-login-atau-ditangguhkan',
      category: 'akun-login-rintisan',
      tab: 'faq',
      question: 'Mengapa akun saya tidak bisa login atau ditangguhkan?',
      answer: `<p>Ada beberapa kemungkinan penyebab akun tidak bisa diakses:</p>
<ul class="list-disc list-inside space-y-2 mt-2">
  <li>Email belum diverifikasi setelah pendaftaran</li>
  <li>Kata sandi yang dimasukkan salah</li>
  <li>Akun dinonaktifkan sementara karena melanggar ketentuan layanan</li>
</ul>
<p class="mt-4">Jika akun kamu ditangguhkan, lakukan Aktivasi Ulang Akun dengan langkah berikut:</p>
<ol class="list-decimal list-inside space-y-2 mt-2">
  <li>Klik opsi <strong>Aktivasi Ulang Akun</strong> di halaman login</li>
  <li>Masukkan email yang terdaftar</li>
  <li>Klik <strong>Kirim</strong> — link verifikasi akan dikirim ke email kamu</li>
  <li>Buka email dan klik link <strong>aktivasi</strong></li>
</ol>
<p class="mt-4">Jika masalah berlanjut, hubungi tim support kami.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'verifikasi-email-saya-gagal-atau-tidak-masuk-apa-yang-harus-saya-lakukan',
      category: 'akun-login-rintisan',
      tab: 'faq',
      question: 'Verifikasi email saya gagal atau tidak masuk. Apa yang harus saya lakukan?',
      answer: `<p>Jika email verifikasi tidak kunjung masuk, coba langkah berikut:</p>
<ol class="list-decimal list-inside space-y-2 mt-2">
  <li>Cek folder <strong>Spam</strong> atau <strong>Promosi</strong> di email kamu</li>
  <li>Pastikan email yang didaftarkan sudah benar</li>
  <li>Tunggu beberapa menit — email terkadang sedikit terlambat</li>
  <li>Jika masih tidak ada, kembali ke halaman <strong>Aktivasi Ulang Akun</strong> dan <strong>kirim ulang link verifikasi</strong></li>
</ol>
<p class="mt-4">Jika tetap bermasalah, hubungi tim support kami dengan menyertakan email yang kamu gunakan untuk mendaftar.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-mengubah-kata-sandi-akun-saya',
      category: 'akun-login-rintisan',
      tab: 'faq',
      question: 'Bagaimana cara mengubah kata sandi akun saya?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Masuk ke <strong>Pengaturan</strong></li>
  <li>Pilih tab <strong>Kata Sandi</strong></li>
  <li>Isi Kata Sandi Saat Ini, Kata Sandi Baru, dan Konfirmasi Kata Sandi Baru</li>
  <li>Klik <strong>Simpan Kata Sandi</strong></li>
</ol>
<p class="mt-4">Pastikan kata sandi baru kamu cukup kuat dan tidak disebarkan ke siapapun.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'saya-lupa-kata-sandi-bagaimana-cara-meresetnya',
      category: 'akun-login-rintisan',
      tab: 'faq',
      question: 'Saya lupa kata sandi. Bagaimana cara meresetnya?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Buka halaman Pengaturan → <strong>Kata Sandi</strong></li>
  <li>Klik Lupa kata sandi? di pojok kanan atas</li>
  <li>Ikuti instruksi yang dikirim ke email terdaftar kamu untuk membuat kata sandi baru</li>
</ol>`,
      updatedAt: '23/02/2026',
    },

    // Profil & Pengaturan Bisnis
    {
      slug: 'bagaimana-cara-mengubah-informasi-profil-bisnis-saya',
      category: 'profil-pengaturan-bisnis',
      tab: 'faq',
      question: 'Bagaimana cara mengubah informasi profil bisnis saya?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Masuk ke Pengaturan → Profil</li>
  <li>Ubah informasi yang diperlukan: nama, nomor telepon, atau alamat bisnis</li>
  <li>Klik Simpan Profil</li>
</ol>
<p class="mt-4">Perubahan akan langsung tersimpan dan berlaku untuk invoice yang dibuat setelahnya.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-mengunggah-atau-mengganti-foto-profil',
      category: 'profil-pengaturan-bisnis',
      tab: 'faq',
      question: 'Bagaimana cara mengunggah atau mengganti foto profil?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Masuk ke Pengaturan → Profil</li>
  <li>Klik Upload Gambar Baru</li>
  <li>Pilih file foto dari perangkat kamu</li>
</ol>
<p class="mt-4">Format yang didukung: <strong>JPG, JPEG, atau PNG</strong>. Ukuran maksimal file adalah <strong>500kb</strong>. Jika ingin menghapus foto, klik <strong>Hapus Gambar</strong>.</p>`, // [AMBIGUOUS] img 13: ada "4." tapi teksnya kosong/terpotong — cuma 3 langkah atau ada step ke-4?
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-memasang-logo-di-invoice-saya',
      category: 'profil-pengaturan-bisnis',
      tab: 'faq',
      question: 'Bagaimana cara memasang logo di invoice saya?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Masuk ke Pengaturan → <strong>Logo Invoice</strong></li>
  <li>Klik area upload dan pilih file logo dari perangkat kamu</li>
  <li>Aktifkan toggle Atur sebagai default agar logo otomatis muncul di setiap invoice baru</li>
  <li>Klik Simpan Pengaturan</li>
</ol>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'di-mana-saya-bisa-mengatur-rekening-bank-untuk-pencairan-dana',
      category: 'profil-pengaturan-bisnis',
      tab: 'faq',
      question: 'Di mana saya bisa mengatur rekening bank untuk pencairan dana?',
      answer: `<p>Masuk ke Pengaturan → <strong>Akun Bank.</strong> Di sini kamu bisa menambahkan atau memperbarui informasi rekening bank yang digunakan untuk menerima pencairan hasil transaksi.</p>
<p class="mt-4">Pastikan rekening yang didaftarkan adalah <strong>rekening reguler</strong>, bukan rekening virtual, agar proses pencairan berjalan lancar.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'apakah-perubahan-profil-berlaku-untuk-invoice-yang-sudah-dikirim',
      category: 'profil-pengaturan-bisnis',
      tab: 'faq',
      question: 'Apakah perubahan profil berlaku untuk invoice yang sudah dikirim?',
      answer: `<p>Tidak. Perubahan data profil dan logo hanya berlaku untuk invoice yang dibuat setelah perubahan disimpan. Invoice yang sudah terkirim sebelumnya tidak akan berubah secara otomatis.</p>`,
      updatedAt: '23/02/2026',
    },

    // Penagihan & Pembayaran Invoice
    {
      slug: 'kapan-pencairan-dana-akan-dilakukan-di-rintisan',
      category: 'penagihan-pembayaran-invoice',
      tab: 'faq',
      question: 'Kapan pencairan dana akan dilakukan di Rintisan?',
      answer: `<p>Dana dari pembayaran klien akan dicairkan ke rekening kamu pada <strong>H+1 Hari Kerja pukul 13.00–16.00</strong>. Jadwal pencairan ini selalu tampil di bagian atas halaman Pembayaran dan Penagihan sebagai pengingat harian.</p>
<p class="mt-4">Contoh: Jika klien membayar pada hari Senin, dana akan cair pada hari Selasa pukul 13.00–16.00 (selama bukan hari libur).</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'apa-yang-harus-dilakukan-jika-mengalami-gagal-3ds',
      category: 'penagihan-pembayaran-invoice',
      tab: 'faq',
      question: 'Apa yang harus dilakukan jika mengalami gagal 3DS?',
      answer: `<p>3DS (3D Secure) adalah verifikasi keamanan tambahan dari bank saat klien membayar menggunakan kartu kredit. Jika proses ini gagal, kemungkinan penyebabnya adalah:</p>
<ul class="list-disc list-inside space-y-2 mt-2">
  <li>Kode OTP dari bank tidak dimasukkan tepat waktu</li>
  <li>Nomor telepon yang terdaftar di bank tidak aktif</li>
  <li>Limit kartu kredit klien tidak mencukupi</li>
  <li>Kartu kredit klien belum diaktifkan untuk transaksi online</li>
</ul>
<p class="mt-4">Sarankan klien kamu untuk menghubungi bank penerbit kartu kredit mereka untuk informasi lebih lanjut. Jika masalah berlanjut, hubungi tim support Rintisan dengan menyertakan nomor invoice dan screenshot error yang muncul.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'mengapa-pembayaran-online-kartu-kredit-klien-saya-gagal',
      category: 'penagihan-pembayaran-invoice',
      tab: 'faq',
      question: 'Mengapa pembayaran online (kartu kredit) klien saya gagal?',
      answer: `<p>Ada beberapa kemungkinan penyebab pembayaran kartu kredit gagal:</p>
<ul class="list-disc list-inside space-y-2 mt-2">
  <li>Kartu kredit belum diaktifkan untuk transaksi online</li>
  <li>Verifikasi 3DS tidak berhasil diselesaikan</li>
  <li>Limit kartu kredit tidak mencukupi</li>
  <li>Kartu sudah expired atau diblokir oleh bank</li>
</ul>
<p class="mt-4">Dalam kasus ini, sarankan klien kamu untuk menghubungi bank penerbit kartu mereka, atau menggunakan metode pembayaran lain yang tersedia di invoice (seperti QRIS atau Tokopedia non-CC).</p>
<p class="mt-4">Anda akan mendapatkan pesan kesalahan yang menginformasikan penyebab spesifiknya. Jika Anda membutuhkan bantuan lebih lanjut untuk memahami pesan tersebut, silakan hubungi tim dukungan kami dengan menyertakan kode kesalahan yang muncul.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'berapa-limit-transaksi-di-rintisan',
      category: 'penagihan-pembayaran-invoice',
      tab: 'faq',
      question: 'Berapa limit transaksi di Rintisan?',
      answer: `<p>Rintisan Billing <strong>tidak menetapkan limit transaksi</strong> dari sisi platform. Kamu bisa membuat invoice dan menerima pembayaran dengan nominal berapapun sesuai kebutuhan bisnis kamu.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'berapa-limit-transaksi-untuk-pembayaran-online-dengan-kartu-kredit-di-rintisan',
      category: 'penagihan-pembayaran-invoice',
      tab: 'faq',
      question: 'Berapa limit transaksi untuk pembayaran online dengan kartu kredit di Rintisan?',
      answer: `<p>Limit transaksi untuk pembayaran online dengan kartu kredit disesuaikan dengan kebijakan bank penerbit kartu Anda. Namun, Rintisan juga memiliki limit transaksi tertentu yaitu 100 juta per invoice yang dibuat.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'berapa-rate-mdr-di-rintisan',
      category: 'penagihan-pembayaran-invoice',
      tab: 'faq',
      question: 'Berapa rate MDR di Rintisan?',
      answer: `<p>Berikut adalah daftar rate MDR untuk setiap metode pembayaran yang tersedia di Rintisan Billing:</p>`,
      answerComponent: 'MdrRateTable',
      updatedAt: '23/02/2026',
    },
    {
      slug: 'apa-perbedaan-invoice-pembayaran-dan-invoice-tagihan-di-rintisan',
      category: 'penagihan-pembayaran-invoice',
      tab: 'faq',
      question: 'Apa perbedaan Invoice Pembayaran dan Invoice Tagihan di Rintisan?',
      answer: `<p>Rintisan Billing memiliki dua jenis invoice yang berbeda fungsinya:</p>
<p class="mt-4">Gunakan menu Pembayaran saat kamu perlu membayar tagihan ke supplier. Gunakan menu Penagihan saat kamu ingin mengirim invoice dan menerima pembayaran dari klien.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'apakah-rintisan-menyediakan-fitur-dp-atau-cicilan',
      category: 'penagihan-pembayaran-invoice',
      tab: 'faq',
      question: 'Apakah Rintisan menyediakan fitur DP atau cicilan?',
      answer: `<p>Rintisan Billing tidak menyediakan fitur DP atau cicilan dari platform secara langsung. Namun, klien kamu tetap bisa melakukan pembayaran bertahap melalui opsi Paylater yang disediakan oleh mitra e-commerce, yaitu Blibli Paylater, jika memilih metode pembayaran tersebut saat membayar invoice.</p>`,
      updatedAt: '23/02/2026',
    },

    // Layanan dan Fitur Rintisan
    {
      slug: 'apa-saja-menu-utama-yang-tersedia-di-rintisan-billing',
      category: 'layanan-dan-fitur-rintisan',
      tab: 'faq',
      question: 'Apa saja menu utama yang tersedia di Rintisan Billing?',
      answer: `<p>Rintisan Billing memiliki 6 menu utama:</p>
<ul class="list-disc list-inside space-y-2 mt-2">
  <li>Dashboard — Ringkasan transaksi, grafik pembayaran harian, dan metode pembayaran yang digunakan</li>
  <li>Pembayaran — Buat dan kelola pembayaran ke supplier</li>
  <li>Penagihan — Buat dan kelola invoice ke klien</li>
  <li>Check SLIK — Cek riwayat kredit seseorang melalui BI Checking</li>
  <li>Partner Deals — Akses penawaran eksklusif dari mitra Rintisan</li>
  <li>Afiliasi — Kelola program referral dan poin RINT kamu</li>
</ul>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'apa-itu-fitur-check-slik-dan-bagaimana-cara-menggunakannya',
      category: 'layanan-dan-fitur-rintisan',
      tab: 'faq',
      question: 'Apa itu fitur Check SLIK dan bagaimana cara menggunakannya?',
      answer: `<p>Check SLIK adalah fitur untuk mengecek riwayat kredit seseorang berdasarkan data BI Checking. Fitur ini berguna untuk menilai kelayakan kredit klien atau mitra bisnis kamu sebelum bertransaksi.</p>
<p class="mt-4">Cara menggunakannya:</p>
<ol class="list-decimal list-inside space-y-2 mt-2">
  <li>Klik menu Check SLIK → Check SLIK</li>
  <li>Isi form: nama sesuai KTP, upload foto KTP, nomor KTP, NPWP, tanggal lahir, jenis kelamin, nomor telepon, email, dan alamat lengkap</li>
  <li>Klik Pratinjau untuk mengecek data sebelum submit</li>
  <li>Setelah selesai, hasil bisa diunduh atau dikirimkan ke email</li>
</ol>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-membuat-invoice-penagihan-ke-klien',
      category: 'layanan-dan-fitur-rintisan',
      tab: 'faq',
      question: 'Bagaimana cara membuat invoice (penagihan) ke klien?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Klik menu <strong>Penagihan → Buat Penagihan Baru</strong></li>
  <li>Pilih atau tambahkan klien yang dituju</li>
  <li>Isi detail invoice: nomor invoice, tanggal, jatuh tempo, deskripsi item, qty, harga, diskon, dan pajak</li>
  <li>Pilih metode pembayaran yang ingin kamu aktifkan untuk klien</li>
  <li>Tambahkan catatan atau pesan jika diperlukan (opsional)</li>
  <li>Klik <strong>Pratinjau Penagihan</strong> untuk mengecek sebelum dikirim</li>
  <li>Klik <strong>Kirim Invoice</strong> — klien akan menerima link pembayaran via WhatsApp atau email</li>
</ol>
<p class="mt-4">Setelah klien melakukan pembayaran, status invoice akan otomatis berubah dan dana akan dicairkan ke rekening kamu pada H+1 hari kerja.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-membuat-pembayaran-ke-supplier',
      category: 'layanan-dan-fitur-rintisan',
      tab: 'faq',
      question: 'Bagaimana cara membuat pembayaran ke supplier?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Klik menu Pembayaran → Buat Pembayaran Baru</li>
  <li>Pilih supplier yang dituju dari daftar</li>
  <li>Upload logo jika diperlukan</li>
  <li>Isi detail: nomor invoice, tanggal, jatuh tempo, deskripsi item, qty, harga, diskon, dan pajak</li>
  <li>Tambahkan catatan jika ada (opsional)</li>
  <li>Klik Pratinjau Pembayaran</li>
</ol>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-menambahkan-supplier',
      category: 'layanan-dan-fitur-rintisan',
      tab: 'faq',
      question: 'Bagaimana cara menambahkan supplier?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Klik menu Pembayaran → Kelola Supplier</li>
  <li>Klik + Tambah Supplier Baru</li>
  <li>Isi detail supplier: pilih grup, nama supplier, alamat, bank, nomor rekening, dan nomor telepon</li>
  <li>Klik Tambahkan Supplier</li>
</ol>
<p class="mt-4">Perhatikan: Setiap akun memiliki limit 5 supplier per grup. Gunakan rekening reguler (bukan rekening virtual) untuk menghindari gagal pengiriman dana.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'apakah-saya-bisa-mengekspor-data-transaksi',
      category: 'layanan-dan-fitur-rintisan',
      tab: 'faq',
      question: 'Apakah saya bisa mengekspor data transaksi?',
      answer: `<p>Ya. Di halaman Pembayaran maupun Penagihan, klik tombol Ekspor Data ke CSV. Kamu bisa memfilter data berdasarkan periode: hari ini, 7 hari terakhir, atau 30 hari terakhir sebelum mengekspor.</p>`,
      updatedAt: '23/02/2026',
    },

    // Afiliasi & Poin
    {
      slug: 'apa-itu-program-afiliasi-rintisan-dan-bagaimana-cara-kerjanya',
      category: 'afiliasi-poin',
      tab: 'faq',
      question: 'Apa itu program Afiliasi Rintisan dan bagaimana cara kerjanya?',
      answer: `<p>Program Afiliasi memungkinkan kamu mendapatkan komisi berupa poin RINT setiap kali pengguna yang mendaftar menggunakan kode referral kamu melakukan transaksi. Semakin banyak transaksi afiliasi kamu, semakin banyak poin RINT yang terkumpul dan semakin tinggi tier yang bisa kamu capai.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'di-mana-saya-bisa-menemukan-kode-referral-saya',
      category: 'afiliasi-poin',
      tab: 'faq',
      question: 'Di mana saya bisa menemukan kode referral saya?',
      answer: `<p>Kode referral kamu tertera di halaman Afiliasi, di bagian bawah kartu informasi tier. Klik ikon copy di sebelah kode untuk menyalinnya, atau klik tombol <strong>Bagikan</strong> untuk langsung membagikannya.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'apa-itu-tier-smart-dan-prestige',
      category: 'afiliasi-poin',
      tab: 'faq',
      question: 'Apa itu tier Smart dan Prestige?',
      answer: `<p>Tier adalah level keanggotaan program afiliasi kamu:</p>
<ul class="list-disc list-inside space-y-2 mt-2">
  <li><strong>Smart</strong> — Tier awal. Mendapatkan multiplier poin 1x untuk setiap transaksi valid dari afiliasi kamu.</li>
  <li><strong>Prestige</strong> — Tier lebih tinggi dengan multiplier poin lebih besar. Untuk mencapai tier Prestige, kamu perlu mengumpulkan 250.000 Poin dan memiliki minimal 10 Afiliasi Aktif.</li>
</ul>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-melihat-daftar-afiliasi-saya',
      category: 'afiliasi-poin',
      tab: 'faq',
      question: 'Bagaimana cara melihat daftar afiliasi saya?',
      answer: `<p>Masuk ke <strong>Afiliasi → Riwayat Afiliasi</strong>. Di sana kamu bisa melihat:</p>
<ul class="list-disc list-inside space-y-2 mt-2">
  <li><strong>Semua Afiliasi</strong> — Daftar seluruh pengguna yang mendaftar menggunakan kode kamu, beserta tanggal bergabung dan total transaksi.</li>
  <li><strong>Afiliasi Aktif</strong> — Hanya menampilkan afiliasi yang sudah bertransaksi, lengkap dengan detail jenis pembayaran dan poin yang kamu dapatkan.</li>
</ul>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-melihat-riwayat-poin-rint-saya',
      category: 'afiliasi-poin',
      tab: 'faq',
      question: 'Bagaimana cara melihat riwayat poin RINT saya?',
      answer: `<p>Masuk ke <strong>Afiliasi → Riwayat Poin</strong>. Di sana tercatat semua aktivitas poin kamu, termasuk:</p>
<ul class="list-disc list-inside space-y-2 mt-2">
  <li>Poin masuk dari transaksi afiliasi</li>
  <li>Poin keluar saat redeem reward</li>
  <li>Detail aktivitas lengkap per transaksi</li>
</ul>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-membeli-rint',
      category: 'afiliasi-poin',
      tab: 'faq',
      question: 'Bagaimana cara membeli RINT?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Klik <strong>Afiliasi → Beli RINT</strong></li>
  <li>Klik tombol <strong>Beli RINT</strong></li>
  <li>Pilih paket RINT yang ingin dibeli (contoh: 20 RINT = Rp 50.000)</li>
  <li>Klik <strong>Lanjutkan</strong> untuk menyelesaikan pembelian</li>
</ol>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-menukarkan-redeem-poin-rint',
      category: 'afiliasi-poin',
      tab: 'faq',
      question: 'Bagaimana cara menukarkan (redeem) poin RINT?',
      answer: `<p>Fitur Reward Store untuk menukarkan poin RINT menjadi produk dan voucher saat ini masih dalam tahap <strong>Coming Soon</strong>. Pantau terus halaman Afiliasi untuk mendapatkan informasi terbaru ketika fitur ini sudah tersedia.</p>`,
      updatedAt: '23/02/2026',
    },

    // Partner Rintisan dan Kasheer
    {
      slug: 'apa-itu-partner-deals',
      category: 'partner-rintisan-dan-kasheer',
      tab: 'faq',
      question: 'Apa itu Partner Deals?',
      answer: `<p>Partner Deals adalah halaman khusus di Rintisan Billing yang berisi penawaran eksklusif dari mitra Rintisan. Semua penawaran bisa diakses langsung dari dalam aplikasi tanpa perlu keluar atau mendaftar di tempat lain.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'penawaran-apa-saja-yang-tersedia-di-partner-deals',
      category: 'partner-rintisan-dan-kasheer',
      tab: 'faq',
      question: 'Penawaran apa saja yang tersedia di Partner Deals?',
      answer: `<p>Saat ini tersedia tiga penawaran:</p>
<ul class="list-disc list-inside space-y-2 mt-2">
  <li><strong>Easycash x Rintisan</strong> — Pinjaman modal usaha hingga Rp 80 juta, bisa dicicil hingga 12 bulan tanpa jaminan dengan bunga kecil</li>
  <li><strong>Kasheer x Rintisan</strong> — Akses aplikasi POS Kasheer dengan harga spesial Rp 2.000.000 (harga normal Rp 3.000.000), bayar sekali pakai selamanya</li>
  <li><strong>QRIS Rintisan</strong> — Daftar QRIS untuk bisnis kamu dengan MDR hingga 0,5%</li>
</ul>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'apa-itu-kasheer-dan-apa-bedanya-dengan-rintisan-billing',
      category: 'partner-rintisan-dan-kasheer',
      tab: 'faq',
      question: 'Apa itu Kasheer dan apa bedanya dengan Rintisan Billing?',
      answer: `<p>Kasheer dan Rintisan Billing adalah dua produk berbeda dari Rintisan:</p>
<ul class="list-disc list-inside space-y-2 mt-2">
  <li><strong>Rintisan Billing</strong> — Aplikasi untuk membuat invoice ke klien dan mengelola pembayaran ke supplier</li>
  <li><strong>Kasheer</strong> — Aplikasi POS (Point of Sale) berbasis tablet yang dirancang khusus untuk bisnis F&amp;B</li>
</ul>
<p class="mt-4">Keduanya bisa digunakan bersamaan untuk kebutuhan bisnis yang lebih lengkap. Pengguna Rintisan Billing mendapatkan harga khusus untuk berlangganan Kasheer melalui menu Partner Deals.</p>`,
      updatedAt: '23/02/2026',
    },
    {
      slug: 'bagaimana-cara-mendaftar-qris-melalui-rintisan',
      category: 'partner-rintisan-dan-kasheer',
      tab: 'faq',
      question: 'Bagaimana cara mendaftar QRIS melalui Rintisan?',
      answer: `<ol class="list-decimal list-inside space-y-2">
  <li>Masuk ke menu <strong>Partner Deals</strong></li>
  <li>Klik banner <strong>Daftar QRIS Rintisan di sini</strong></li>
  <li>Ikuti langkah pendaftaran yang tersedia</li>
</ol>
<p class="mt-4">MDR yang ditawarkan hingga 0,5% — salah satu yang paling kompetitif untuk kebutuhan bisnis kamu.</p>`,
      updatedAt: '23/02/2026',
    },
  ]

  function getCategoryBySlug(slug: string) {
    return categories.find(c => c.slug === slug)
  }

  function getArticlesByCategory(categorySlug: string, tab?: string) {
    return articles.filter((a) => {
      const matchCategory = a.category === categorySlug
      const matchTab = tab ? a.tab === tab : true
      return matchCategory && matchTab
    })
  }

  function getArticle(categorySlug: string, articleSlug: string) {
    return articles.find(a => a.category === categorySlug && a.slug === articleSlug)
  }

  function searchArticles(query: string, categorySlug?: string) {
    const lowerQuery = query.toLowerCase()
    return articles.filter((a) => {
      const matchQuery = a.question.toLowerCase().includes(lowerQuery)
      const matchCategory = categorySlug ? a.category === categorySlug : true
      return matchQuery && matchCategory
    })
  }

  return {
    tabs,
    categories,
    articles,
    getCategoryBySlug,
    getArticlesByCategory,
    getArticle,
    searchArticles,
  }
}
